import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { createWhoWeServeItem } from "@/lib/api/marketing.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { MarketingAdminNav } from "@/components/admin/MarketingAdminNav";
import { ServiceItemEditor } from "@/components/admin/ServiceItemEditor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import type { MediaSelection } from "@/lib/media/types";

export const Route = createFileRoute("/admin/marketing/who-we-serve/new")({
  component: NewWhoWeServePage,
});

function NewWhoWeServePage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState<MediaSelection>({});
  const [status, setStatus] = useState<"draft" | "published">("published");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const row = await createWhoWeServeItem({
        data: {
          name,
          body,
          imageMediaId: image.mediaId,
          imageUrl: image.url,
          status,
        },
      });
      toast.success("Audience added");
      navigate({ to: "/admin/marketing/who-we-serve/$id", params: { id: row.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Add audience" />
      <MarketingAdminNav />
      <div className="space-y-4">
        <ServiceItemEditor
          name={name}
          description={body}
          image={image}
          onNameChange={setName}
          onDescriptionChange={setBody}
          onImageChange={setImage}
        />
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <AdminFormFooter
        cancelTo="/admin/marketing/who-we-serve"
        onSave={handleSave}
        saving={saving}
        saveLabel="Create audience"
        savingLabel="Creating…"
      />
    </AdminPageShell>
  );
}
